import type { Chart, ChartData, Plugin } from "chart.js";

/**
 * Lets a chart with months of history scroll sideways while its y-axis,
 * legend and labels stay put: the chart keeps its normal width and draws a
 * window of the bars that fit (perBar px each), and a scrollbar under it -
 * or a sideways swipe on a trackpad over the chart - moves that window.
 *
 * The window is a slice of the data rather than axis limits, so bar labels
 * (datalabels) and stack totals exist only for the bars on show, and the
 * y-axis fits them.
 *
 * It opens at the newest end, and goes back there when new data arrives
 * (another market, a refresh). With few enough bars it does nothing.
 */
interface ScrollState {
  /** The whole history, as the dashboard last handed it over. */
  full: ChartData;
  /** The labels array of the slice on show: anything else in chart.data is new data. */
  shown: unknown[] | null;
  start: number;
  visible: number;
  /** labels.length and the last label when the window was set: new data resets it. */
  signature: string;
  /**
   * The dataset chart.js is shown for each of the dashboard's datasets: the same
   * object every update, with just its data swapped for the slice - chart.js keys a
   * dataset's state (a series hidden from the legend, say) to the dataset object.
   */
  views: WeakMap<object, object>;
  bar: HTMLDivElement;
  track: HTMLDivElement;
  syncing: boolean;
}

type ScrollChart = Chart & { $fhScroll?: ScrollState };

export function scrollWindow(perBar: number): Plugin {
  return {
    id: "fhScrollWindow",

    beforeUpdate(chart: ScrollChart) {
      const data = chart.data;
      const state = chart.$fhScroll ?? attach(chart);
      // ng2-charts copies new data into this same object; if its labels are not
      // our slice, the dashboard has handed over a new history.
      if (data.labels !== state.shown) {
        state.full = { labels: data.labels ?? [], datasets: data.datasets };
      }
      const labels = state.full.labels ?? [];
      const total = labels.length;
      // The plot's width from the last layout (the first one estimates it).
      const plotWidth = chart.chartArea
        ? chart.chartArea.right - chart.chartArea.left
        : chart.width - 80;
      state.visible = Math.max(1, Math.floor(plotWidth / perBar));

      if (total <= state.visible) {
        data.labels = labels;
        data.datasets = viewsOf(state, 0, total);
        state.shown = labels;
        return;
      }

      const signature = `${total}|${String(labels[total - 1])}`;
      if (signature !== state.signature) {
        state.signature = signature;
        state.start = total - state.visible;
      }
      state.start = Math.min(Math.max(0, state.start), total - state.visible);
      const end = state.start + state.visible;
      data.labels = labels.slice(state.start, end);
      data.datasets = viewsOf(state, state.start, end);
      state.shown = data.labels;
    },

    afterUpdate(chart: ScrollChart) {
      const state = chart.$fhScroll;
      if (!state) return;
      const total = state.full.labels?.length ?? 0;
      if (total <= state.visible) {
        state.bar.style.display = "none";
        return;
      }
      // The scrollbar mirrors the window: one perBar of scroll per bar.
      state.bar.style.display = "block";
      state.track.style.width = `${total * perBar}px`;
      state.syncing = true;
      state.bar.scrollLeft = state.start * perBar;
      requestAnimationFrame(() => (state.syncing = false));
    },

    afterDestroy(chart: ScrollChart) {
      chart.$fhScroll?.bar.remove();
      delete chart.$fhScroll;
    },
  };

  /** Each dataset's stable view, showing its data from start to end. */
  function viewsOf(state: ScrollState, start: number, end: number): ChartData["datasets"] {
    return state.full.datasets.map((d) => {
      let view = state.views.get(d);
      if (!view) {
        view = {};
        state.views.set(d, view);
      }
      // Its other settings follow the dashboard's dataset; only the data is a slice.
      return Object.assign(view, d, { data: (d.data as unknown[]).slice(start, end) });
    }) as ChartData["datasets"];
  }

  function attach(chart: ScrollChart): ScrollState {
    const bar = document.createElement("div");
    bar.className = "chart-scrollbar";
    bar.style.display = "none";
    const track = document.createElement("div");
    track.className = "chart-scrollbar-track";
    bar.appendChild(track);
    // Under the chart's own box (the canvas's wrapper), so the canvas keeps its height.
    chart.canvas.parentElement?.after(bar);

    const state: ScrollState = {
      full: { labels: [], datasets: [] },
      shown: null,
      start: 0,
      visible: 0,
      signature: "",
      views: new WeakMap(),
      bar,
      track,
      syncing: false,
    };
    chart.$fhScroll = state;

    bar.addEventListener("scroll", () => {
      if (state.syncing) return;
      const start = Math.round(bar.scrollLeft / perBar);
      if (start === state.start) return;
      state.start = start;
      chart.update("none");
    });
    // A sideways swipe over the chart scrolls it; an up-down one still scrolls the page.
    chart.canvas.addEventListener(
      "wheel",
      (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || bar.style.display === "none") return;
        e.preventDefault();
        bar.scrollLeft += e.deltaX;
      },
      { passive: false }
    );
    return state;
  }
}
